import { useEffect, useState } from "react";
import { IAddressFieldsInput, IAreaDetailInput, IFieldValidationInput } from "@types";
import { commonApi } from "gate";
import useRequestImmutable from "@hooks/useRequestImmutable";

function useAddressFormValue(d: {
  area: IAreaDetailInput;
  defaultValue?: IAddressFieldsInput;
  id?: number;
  setMapPosition?: (data: { lat: number; lng: number; street: string }) => void;
  setUseMap?: (data: boolean) => void;
  useMap?: boolean;
  onSubmit: (data: IAddressFieldsInput) => void;
}) {
  const { area, defaultValue, id, setMapPosition, setUseMap } = d;
  const { data: res } = useRequestImmutable(commonApi.getAddressFields());

  const [value, setValue] = useState<{ [key: string]: any }>({});
  const [types, setTypes] = useState<{
    [key: string]: "map" | "area" | "text" | "number" | "phone";
  }>({});
  const [defaultValidations, setValidations] = useState<{
    [key: string]: IFieldValidationInput[];
  }>({});
  const [data, setData] = useState<IAddressFieldsInput>();
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (defaultValue && id) {
      processDefaultValue(defaultValue, area, setMapPosition, setUseMap);
    } else if (res?.data) {
      processResData(res.data, area);
    }
  }, [res, defaultValue, id]);

  function processDefaultValue(
    defaultValue: IAddressFieldsInput,
    area: IAreaDetailInput,
    setMapPosition?: (data: { lat: number; lng: number; street: string }) => void,
    setUseMap?: (data: boolean) => void
  ) {
    let _value = {};
    let _validation = {};
    let _errors = {};
    let _types = {};
    for (const row of defaultValue.items) {
      for (const field of row.items) {
        if (field.type === "map" && field.value) {
          setMapPosition?.({
            lat: Number(field.value.split(",")[0]),
            lng: Number(field.value.split(",")[1]),
            street: "",
          });
          setUseMap?.(true);
        }
        _value = {
          ..._value,
          [`${field.key}`]: field.type === "area" ? area.id : field.value,
        };
        _validation = { ..._validation, [`${field.key}`]: field.validations };
        _errors = { ..._errors, [`${field.key}`]: [] };
        _types = { ..._types, [`${field.key}`]: field.type };
      }
    }
    setData(defaultValue);
    setValue(_value);
    setErrors(_errors);
    setTypes(_types);
    setValidations(_validation);
    setIsLoading(false);
  }

  function processResData(resData: IAddressFieldsInput, area: IAreaDetailInput) {
    let _value = {};
    let _validation = {};
    let _errors = {};
    let _types = {};
    for (const row of resData.items) {
      for (const field of row.items) {
        _value = {
          ..._value,
          [`${field.key}`]: field.type === "area" ? `${area?.id}` : "",
        };
        _validation = { ..._validation, [`${field.key}`]: field.validations };
        _errors = { ..._errors, [`${field.key}`]: [] };
        _types = { ..._types, [`${field.key}`]: field.type };
      }
    }
    setData(resData);
    setValue(_value);
    setErrors(_errors);
    setTypes(_types);
    setValidations(_validation);
    setIsLoading(false);
  }

  return {
    value,
    setValue,
    types,
    setTypes,
    defaultValidations,
    setValidations,
    data,
    setData,
    isValid,
    setIsValid,
    errors,
    setErrors,
    isLoading,
    setIsLoading,
  };
}

export default useAddressFormValue;
